import { useCallback } from "react";
import { roleConfigs, type AppRole } from "@/components/portal/RoleSwitcher";
import { ACTIVE_ROLE_KEY } from "@/hooks/useAuth";

/**
 * Role priority for post-login routing (highest priority last).
 * When a user has multiple roles, we pick the highest-priority one.
 * Admin lands on /dashboard; portals are lower priority.
 * super_admin is a cross-company role — admin takes priority for
 * company-level dashboard context.
 */
const ROLE_PRIORITY = [
  "service_provider",
  "tenant",
  "employee",
  "owner",
  "super_admin",
  "admin",
] as const satisfies readonly AppRole[];

/**
 * Hook that determines the post-login navigation path based on user roles.
 * Sets the active role in sessionStorage and returns the portal/dashboard path.
 */
export function usePortalRouter() {
  const getPostLoginPath = useCallback((roles: string[]): string => {
    if (roles.length === 0) {
      return "/no-access";
    }

    const knownRoles = roles.filter((r): r is AppRole => r in roleConfigs);
    if (knownRoles.length === 0) {
      return "/no-access";
    }

    // Single role — use it directly
    let selected: AppRole = knownRoles[0];

    // Multiple roles — pick highest priority
    if (knownRoles.length > 1) {
      for (const candidate of ROLE_PRIORITY) {
        if (knownRoles.includes(candidate)) {
          selected = candidate;
        }
      }
    }

    sessionStorage.setItem(ACTIVE_ROLE_KEY, selected);

    const path = roleConfigs[selected].path;
    // roleConfigs uses "/" for admin — map to /dashboard
    return path === "/" ? "/dashboard" : path;
  }, []);

  return { getPostLoginPath };
}
